/**
 * Customer wire ⇄ domain mappers. Generic deep case conversion plus explicit
 * fixups for acronym fields (`assignedCA`, `assignedCAName`) that don't
 * round-trip through the generic converter.
 *
 * NOTE: confirm field names against the live customer-service contract during
 * real-mode verification and adjust the overrides here only.
 */

import { toCamelDeep, toSnakeDeep } from '@/services/http/caseMap'
import type { IndividualCustomer, CreateCustomerInput, UpdateCustomerInput } from '@/types/customer'

export function mapCustomerFromApi(raw: Record<string, unknown>): IndividualCustomer {
  const camel = toCamelDeep<Record<string, unknown>>(raw)
  camel.id = String(camel.id)
  // Optional profile data must retain its absence; collections and address need safe shapes.
  camel.residentialAddress = camel.residentialAddress ?? {}
  for (const collection of [
    'properties',
    'vehicles',
    'investments',
    'bankAccounts',
    'assessments',
  ]) {
    if (!Array.isArray(camel[collection])) camel[collection] = []
  }
  // Fix acronym fields the generic converter lowercases.
  if ('assignedCa' in camel) {
    camel.assignedCA = camel.assignedCa
    delete camel.assignedCa
  }
  if ('assignedCaName' in camel) {
    camel.assignedCAName = camel.assignedCaName
    delete camel.assignedCaName
  }
  return camel as unknown as IndividualCustomer
}

export function mapCustomerListFromApi(raw: unknown): IndividualCustomer[] {
  // Accept either a bare array or a { items: [...] } envelope.
  const arr = Array.isArray(raw) ? raw : ((raw as { items?: unknown[] })?.items ?? [])
  return (arr as Record<string, unknown>[]).map(mapCustomerFromApi)
}

export function mapCustomerCreateToApi(input: CreateCustomerInput): Record<string, unknown> {
  return toSnakeDeep(input)
}

export function mapCustomerUpdateToApi(input: UpdateCustomerInput): Record<string, unknown> {
  return toSnakeDeep(input)
}
